import { effectiveAccessQueryOptions } from "@/api/access/access.queries";
import {
  modulePermissionsQueryOptions,
  type RouterContextType,
} from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import SPWorkOrderDetails from "@/components/features/WorkOrders/SPWorkOrder/SPWorkOrderDetails";
import WorkOrderHeader from "@/components/features/WorkOrders/WorkOrderHeader/WorkOrderHeader";
import WOChatTab from "@/components/features/WorkOrders/WorkOrderTabs/Chat/WOChatTab";
import WOOverviewTab from "@/components/features/WorkOrders/WorkOrderTabs/Overview/WOOverviewTab";
import WOVisitsTab from "@/components/features/WorkOrders/WorkOrderTabs/Visits/WOVisitsTab";
import { WOFilesTab } from "@/components/features/FileManager/mounts/WOFilesTab";
import { NewProposalFlow } from "@/components/features/WorkOrders/NewProposalModal/NewProposalFlow";
import ProposalsTab from "@/components/features/WorkOrders/WorkOrderTabs/Proposals/ProposalsTab";
import { WORequestToPayTab } from "@/components/features/AccountsPayable/RequestToPayTab/WORequestToPayTab";
import { WOProcurementTab } from "@/components/features/Procurement/WOTab/WOProcurementTab";
import { WorkOrderDetailSkeleton } from "@/components/ui/Skeleton";
import { useSwitchOr } from "@/hooks/access/useSwitchOr";
import { useModulePermissions } from "@/hooks/auth/useModulePermissions";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { getDestinationScope } from "@/hooks/useRequestScope";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { useGetWorkOrderHeaderData } from "@/hooks/work-orders/details/useOverviewQueries";
import type { TabItemType } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Flex, Tabs } from "@mantine/core";
import { TabsScrollArea } from "@/components/ui/Tab/TabsScrollArea";
import { useDisclosure } from "@mantine/hooks";
import { createFileRoute, retainSearchParams } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import styles from "./workOrderId.module.scss";

const LAZY_TAB_VALUES = new Set(["chat", "requestToPay", "procurement", "files"]);

export const Route = createFileRoute("/_authenticated/work-orders/$workOrderId/")({
  component: WorkOrderDetails,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
  loader: ({ context, location }) => {
    const ctx = context as unknown as RouterContextType;
    const scope = getDestinationScope(location.search as Record<string, unknown>);
    return Promise.all([
      ctx.queryClient.ensureQueryData(
        modulePermissionsQueryOptions(scope, MODULE_KEYS_MAP.workOrder),
      ),
      ctx.queryClient.prefetchQuery({ ...effectiveAccessQueryOptions(scope), retry: false }),
    ]);
  },
});

function WorkOrderDetails() {
  const { workOrderId } = Route.useParams();

  const { isServiceProvider, hasAccess } = usePermissions();
  const { hasSubAccess } = useModulePermissions(
    MODULE_KEYS_MAP.workOrder,
    MODULE_KEYS_MAP.workOrders,
  );
  const { view: canViewOverview } = hasSubAccess("work_order.overview");
  const { view: canViewVisitDetail } = hasSubAccess("visit.detail");
  const { view: canViewProcurement } = hasAccess({ module: MODULE_KEYS_MAP.procurement });
  // SERV-3580: the tab's list read asks both switches (proposal/app.ts, alsoSwitches).
  const holdsProposalView = useSwitchOr("proposal", "view", true);
  const holdsWorkOrderProposalsView = useSwitchOr("work_order.proposals", "view", true);
  const canViewProposals = holdsProposalView && holdsWorkOrderProposalsView;
  const canViewRequestToPay = useSwitchOr("work_order.request_to_pay", "view", true);
  const canViewChat = useSwitchOr("work_order.chat", "view", true);
  const canViewFinancials = useSwitchOr("work_order.overview.financials", "view", true);

  const defaultTab = canViewOverview
    ? "overview"
    : canViewVisitDetail
      ? "visits"
      : canViewProposals
        ? "workOrderProposals"
        : "files";
  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab });
  const { data: workOrderHeaderData, isLoading } = useGetWorkOrderHeaderData(workOrderId);
  const [newProposalModalOpened, newProposalModalHandlers] = useDisclosure(false);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Work Orders",
        href: "/work-orders",
        icon: getRouteIcon(MODULE_KEYS_MAP.workOrders),
      },
      {
        title: workOrderHeaderData?.data?.workOrderNumber || "Loading...",
        href: `/work-orders/${workOrderId}`,
      },
    ],
    [workOrderHeaderData?.data?.workOrderNumber, workOrderId],
  );

  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  const tabsData: TabItemType[] = useMemo(
    () => [
      ...(canViewOverview
        ? [
            {
              value: "overview",
              label: "Overview",
              component: <WOOverviewTab />,
            },
          ]
        : []),
      ...(canViewVisitDetail
        ? [
            {
              value: "visits",
              label: "Visits",
              component: <WOVisitsTab />,
            },
          ]
        : []),
      ...(canViewProposals
        ? [
            {
              value: "workOrderProposals",
              label: "Work Order Proposals",
              component: (
                <ProposalsTab
                  onNewProposal={newProposalModalHandlers.open}
                  workOrderNumber={workOrderHeaderData?.data?.workOrderNumber ?? undefined}
                />
              ),
            },
          ]
        : []),
      ...(canViewProcurement
        ? [
            {
              value: "procurement",
              label: "Procurement",
              component: <WOProcurementTab />,
            },
          ]
        : []),
      ...(canViewRequestToPay
        ? [
            {
              value: "requestToPay",
              label: "Request to Pay",
              component: <WORequestToPayTab />,
            },
          ]
        : []),
      ...(canViewChat
        ? [
            {
              value: "chat",
              label: "Chat",
              component: (
                <WOChatTab
                  workOrderNumber={workOrderHeaderData?.data?.workOrderNumber ?? undefined}
                />
              ),
            },
          ]
        : []),
      {
        value: "files",
        label: "Files",
        component: <WOFilesTab />,
      },
    ],
    [
      canViewOverview,
      canViewVisitDetail,
      canViewProcurement,
      canViewProposals,
      canViewRequestToPay,
      canViewChat,
      workOrderHeaderData?.data?.workOrderNumber,
    ],
  );

  useEffect(() => {
    if (!canViewOverview && activeTab === "overview") {
      setActiveTab(defaultTab);
    }
    if (!canViewVisitDetail && activeTab === "visits") {
      setActiveTab(defaultTab);
    }
    if (!canViewProcurement && activeTab === "procurement") {
      setActiveTab(defaultTab);
    }
    if (
      (!canViewProposals && activeTab === "workOrderProposals") ||
      (!canViewRequestToPay && activeTab === "requestToPay") ||
      (!canViewChat && activeTab === "chat")
    ) {
      setActiveTab(defaultTab);
    }
    // The Location Info tab was merged into Overview; redirect stale ?tab=locations links.
    if (activeTab === "locations") {
      setActiveTab(defaultTab);
    }
  }, [
    canViewOverview,
    canViewVisitDetail,
    canViewProcurement,
    canViewProposals,
    canViewRequestToPay,
    canViewChat,
    activeTab,
    defaultTab,
    setActiveTab,
  ]);

  // Render function for tabs
  const renderTabs = (tabsData: TabItemType[]) => {
    return (
      <>
        <TabsScrollArea>
          <Tabs.List className={styles.tabsList}>
            {tabsData.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </TabsScrollArea>

        {tabsData.map((tab) => {
          const isLazy = LAZY_TAB_VALUES.has(tab.value);
          const content = isLazy && activeTab !== tab.value ? null : tab.component;

          if (tab.value === "chat") {
            return (
              <Tabs.Panel
                key={tab.value}
                value={tab.value}
                style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
              >
                {content}
              </Tabs.Panel>
            );
          }

          return (
            <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
              {content}
            </Tabs.Panel>
          );
        })}
      </>
    );
  };

  if (isLoading) {
    return <WorkOrderDetailSkeleton />;
  }

  return (
    <Flex
      direction="column"
      style={{
        margin: "calc(var(--mantine-spacing-md) * -1)",
        backgroundColor: "var(--neutral-500)",
        overflow: "auto",
      }}
    >
      <WorkOrderHeader
        internalPriority={workOrderHeaderData?.data?.internalPriority?.label ?? ""}
        currentStatus={workOrderHeaderData?.data?.woStatus?.label ?? ""}
        workOrderNumber={workOrderHeaderData?.data?.workOrderNumber ?? ""}
        address={workOrderHeaderData?.data?.location?.address ?? {}}
        clientName={workOrderHeaderData?.data?.client?.businessName ?? ""}
        /* SERV-2721: brand and site id join the address on the header identity line. */
        brandName={workOrderHeaderData?.data?.brand?.name ?? null}
        siteId={workOrderHeaderData?.data?.location?.siteId ?? null}
        visitsCount={workOrderHeaderData?.data?.visitsCount ?? 0}
        createdAt={workOrderHeaderData?.data?.createdAt ?? ""}
        nte={workOrderHeaderData?.data?.nte ?? null}
        /* SERV-2685: remainingNte was NTE halved. Hidden, not deleted. Restore this
           line together with the interface field and destructure in
           WorkOrderHeader.tsx, or the component will not compile. */
        /* remainingNte={workOrderHeaderData?.data?.remainingNte ?? 0} */
        visitType={workOrderHeaderData?.data?.nextVisit?.visitType?.label ?? ""}
        nextVisitDate={workOrderHeaderData?.data?.nextVisit?.scheduledTime ?? ""}
        assignedSPName={workOrderHeaderData?.data?.assignedServiceProvider ?? null}
        /* 11.R: backup providers removed for the redesign */
        /* backupProvidersCount={workOrderHeaderData?.data?.nextVisit?.VisitProviders?.length ?? 0} */
        isSPLogin={isServiceProvider}
        showFinancials={isServiceProvider || canViewFinancials}
        nextVisitStatus={workOrderHeaderData?.data?.nextVisit?.status?.value ?? ""}
        onNewProposal={newProposalModalHandlers.open}
        isChangeOrder={workOrderHeaderData?.data?.isChangeOrder}
        isRecall={workOrderHeaderData?.data?.isRecall}
        activeTab={activeTab}
      />
      <NewProposalFlow
        isOpen={newProposalModalOpened}
        onClose={newProposalModalHandlers.close}
        workOrderId={workOrderId}
      />
      {!isServiceProvider ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            className={styles.tabs}
            styles={{ root: { height: "100%", display: "flex", flexDirection: "column" } }}
          >
            {renderTabs(tabsData)}
          </Tabs>
        </div>
      ) : (
        <SPWorkOrderDetails workOrderId={workOrderId} />
      )}
    </Flex>
  );
}
